import { trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  viewChild,
} from '@angular/core';
import {
  AssetColorLabel,
  AssetDto,
  AssetStatus,
  GalleryColorLabel,
  GalleryDto,
  GalleryFeedbackOptions,
} from '@nusign/picflow-backend-types';
import { Observable, Subject, filter, of, takeUntil } from 'rxjs';
import { fadeUpIfSubtle } from '../../../animations/fade-up-subtle.if.animation';
import { fadeIf } from '../../../animations/fade.if.animation';
import { NonPreviewFileComponent } from '../../../gallery/components/non-preview-file/non-preview-file.component';
import { CanWatermarkAssetPipe } from '../../../gallery/pipes/can-watermark-asset.pipe';
import { GROWTHBOOK } from '../../../growthbook';
import { AssetFeedbackService } from '../../../services/asset-feedback.service';
import { AssetService } from '../../../services/asset.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { CanDisplayAssetPipe } from '../../pipes/can-display-asset.pipe';
import { AssetFeedbackHintsComponent } from '../asset-feedback-hints/asset-feedback-hints.component';
import { AssetFeedbackComponent } from '../asset-feedback/asset-feedback.component';
import { AudioViewerComponent } from '../audio-viewer/audio-viewer.component';
import { VideoViewerComponent } from '../video-viewer/video-viewer.component';

@Component({
  selector: 'app-layout-asset',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './layout-asset.component.html',
  styleUrl: './layout-asset.component.scss',
  animations: [trigger('assetFeedback', fadeUpIfSubtle), trigger('assetFeedbackHints', fadeIf)],
  imports: [
    AssetVersionUrlPipe,
    VideoViewerComponent,
    CanDisplayAssetPipe,
    SvgComponent,
    LoadingIndicatorComponent,
    NonPreviewFileComponent,
    AssetFeedbackHintsComponent,
    AssetFeedbackComponent,
    CanWatermarkAssetPipe,
  ],
})
export class LayoutAssetComponent implements OnInit, OnDestroy {
  public growthBook = inject(GROWTHBOOK);
  private check = inject(ChangeDetectorRef);
  private assetFeedbackService = inject(AssetFeedbackService);
  private assetService = inject(AssetService);
  readonly asset = input.required<AssetDto>();
  readonly gallery = input.required<GalleryDto>();
  readonly roundedCorners = input.required<boolean>();
  readonly showFilename = input.required<boolean>();
  readonly darkBackground = input.required<boolean>();
  readonly isDesktop = input.required<boolean>();
  readonly hovering = input.required<boolean>();
  readonly width = input.required<number>();
  readonly height = input.required<number>();
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  readonly approvedAssetCount = input.required<number>();
  readonly colorLabels = input.required<GalleryColorLabel[]>();
  readonly clientMode = input.required<boolean>();
  readonly allowedAndEnabledFeedbackOptions = input<GalleryFeedbackOptions[]>([]);
  readonly preview = input<string>(undefined);
  video = viewChild<VideoViewerComponent>('video');
  audio = viewChild<AudioViewerComponent>('audio');

  destroyed$ = new Subject<void>();

  favoritedUsersString$: Observable<string> = of('Loading...');

  ngOnInit(): void {
    this.assetService.updates
      .pipe(
        takeUntil(this.destroyed$),
        filter(asset => asset?.id === this.asset()?.id)
      )
      .subscribe(() => {
        this.check.detectChanges();
      });
  }

  toggleLike() {
    this.assetFeedbackService.toggleLike(this.asset(), this.gallery());
  }

  updateAssetStatus(status: AssetStatus) {
    this.assetFeedbackService.updateAssetStatus(this.asset(), this.gallery(), status, true);
  }
  updateColorLabel(colorLabel: AssetColorLabel) {
    this.assetFeedbackService.updateColorLabel(this.asset(), this.gallery(), colorLabel, true);
  }

  onTimeChange() {
    this.check.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
